import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

interface SEOHeadProps {
  titleKey: string
  descriptionKey?: string
  titleSuffix?: boolean
  /** Optional OG image path (relative to site root) */
  ogImage?: string
  /** Optional JSON-LD structured data */
  jsonLd?: Record<string, unknown>
}

const SITE_NAME = 'Selea'
const DEFAULT_OG_IMAGE = '/media/logos/selea-logo-color.svg'

function setMetaTag(property: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name'
  let meta = document.querySelector(`meta[${attr}="${property}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, property)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

function setJsonLd(data: Record<string, unknown>) {
  const id = 'seo-json-ld'
  let script = document.getElementById(id) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data)
}

/**
 * Manages document head (title, meta description, OG tags, JSON-LD) for SEO.
 * Uses i18n translation keys for multilingual support.
 */
export function SEOHead({
  titleKey,
  descriptionKey,
  titleSuffix = true,
  ogImage,
  jsonLd,
}: SEOHeadProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const lang = i18n.language || 'it'

  useEffect(() => {
    const title = t(titleKey)
    const fullTitle = titleSuffix ? `${title} | ${SITE_NAME}` : title
    document.title = fullTitle

    // Meta description
    const description = descriptionKey ? t(descriptionKey) : ''
    if (description) {
      setMetaTag('description', description)
    }

    // Open Graph tags
    setMetaTag('og:title', fullTitle, true)
    setMetaTag('og:type', 'website', true)
    setMetaTag('og:site_name', SITE_NAME, true)
    setMetaTag('og:locale', lang === 'en' ? 'en_US' : 'it_IT', true)
    if (description) {
      setMetaTag('og:description', description, true)
    }
    if (ogImage) {
      setMetaTag('og:image', ogImage, true)
    } else {
      setMetaTag('og:image', DEFAULT_OG_IMAGE, true)
    }

    // HTML lang attribute
    document.documentElement.lang = lang

    // Canonical
    const canonicalUrl = `${window.location.origin}${location.pathname}`
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = canonicalUrl
    setMetaTag('og:url', canonicalUrl, true)

    // Hreflang tags
    const pathWithoutLang = location.pathname.replace(/^\/(it|en)/, '')
    const itUrl = `${window.location.origin}/it${pathWithoutLang}`
    const enUrl = `${window.location.origin}/en${pathWithoutLang}`

    function setHreflang(hreflang: string, href: string) {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.rel = 'alternate'
        link.hreflang = hreflang
        document.head.appendChild(link)
      }
      link.href = href
    }
    setHreflang('it', itUrl)
    setHreflang('en', enUrl)
    setHreflang('x-default', itUrl)

    // JSON-LD
    if (jsonLd) {
      setJsonLd(jsonLd)
    }
  }, [t, titleKey, descriptionKey, titleSuffix, ogImage, jsonLd, lang, location.pathname])

  return null
}

/**
 * Creates JSON-LD Organization schema for Selea.
 */
export function getOrganizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Selea s.r.l.',
    url: 'https://www.selea.com',
    logo: 'https://www.selea.com/media/logos/selea-logo-color.svg',
    description: 'Italian manufacturer of ANPR/OCR surveillance cameras and license plate reading systems.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via Aldo Moro 69',
      addressLocality: 'Cicognara',
      addressRegion: 'MN',
      addressCountry: 'IT',
    },
    sameAs: [
      'https://www.facebook.com/SeleaSrl',
      'https://www.linkedin.com/company/selea-srl',
      'https://t.me/seleasrl',
    ],
    parentOrganization: {
      '@type': 'Organization',
      name: 'TTM Group',
    },
  }
}

/**
 * Creates JSON-LD BreadcrumbList schema.
 */
export function getBreadcrumbJsonLd(items: { name: string; url: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}
